import React, { Fragment, useState, useEffect, useMemo } from "react";
import Breadcrumb from "../common/breadcrumb";
import { Modal, Card, CardBody, CardHeader, Col, Container, ModalBody, ModalHeader, Row, Table, Input, Label, FormGroup, Button } from "reactstrap";
import DataTables from "../common/DataTables";
import { useDispatch, useSelector } from "react-redux";
import { GetCustomerList } from "../../redux/actions/SettingActions";
import moment from "moment/moment";
import { Eye, RefreshCw } from "react-feather";

const CustomerList = () => {
    const dispatch = useDispatch();
    const { CustomerListdata } = useSelector(state => state.settings);

    const [viewModal, setViewModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Filter states
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [hasOrdersFilter, setHasOrdersFilter] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // Sort states
    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("desc");

    const Capitalize = (str) => {
        return str?.charAt(0).toUpperCase() + str?.slice(1);
    };

    const formatCurrency = (amount) => {
        return `₹ ${parseFloat(amount || 0).toFixed(2)}`;
    };

    const handleView = (customer) => {
        setSelectedCustomer(customer);
        setViewModal(true);
    };

    const closeModal = () => {
        setViewModal(false);
        setSelectedCustomer(null);
    };

    const getFullAddress = (address) => {
        if (!address) return "-";
        return [
            address.address,
            address.location,
            address.state,
            address.country,
            address.pincode
        ].filter(Boolean).join(", ");
    };

    const resetFilters = () => {
        setSearchText("");
        setStatusFilter("");
        setHasOrdersFilter("");
        setDateFrom("");
        setDateTo("");
        setSortField("created_at");
        setSortOrder("desc");
    };

    // Filter and sort the data
    const filteredData = useMemo(() => {
        if (!CustomerListdata) return [];

        let data = [...CustomerListdata];

        // Search filter (name, email, mobile)
        if (searchText) {
            const search = searchText.toLowerCase();
            data = data.filter(customer =>
                customer?.name?.toLowerCase().includes(search) ||
                customer?.email?.toLowerCase().includes(search) ||
                customer?.mobile?.includes(search)
            );
        }

        // Status filter
        if (statusFilter) {
            data = data.filter(customer => customer?.status === statusFilter);
        }

        // Has orders filter
        if (hasOrdersFilter === "with_orders") {
            data = data.filter(customer => (customer?.total_orders || 0) > 0);
        } else if (hasOrdersFilter === "no_orders") {
            data = data.filter(customer => (customer?.total_orders || 0) === 0);
        }

        // Date range filter
        if (dateFrom) {
            const fromDate = moment(dateFrom).startOf('day');
            data = data.filter(customer =>
                moment(customer?.created_at).isSameOrAfter(fromDate)
            );
        }
        if (dateTo) {
            const toDate = moment(dateTo).endOf('day');
            data = data.filter(customer =>
                moment(customer?.created_at).isSameOrBefore(toDate)
            );
        }

        // Sorting
        data.sort((a, b) => {
            let aValue, bValue;

            switch (sortField) {
                case "name":
                    aValue = a?.name?.toLowerCase() || "";
                    bValue = b?.name?.toLowerCase() || "";
                    break;
                case "email":
                    aValue = a?.email?.toLowerCase() || "";
                    bValue = b?.email?.toLowerCase() || "";
                    break;
                case "total_orders":
                    aValue = a?.total_orders || 0;
                    bValue = b?.total_orders || 0;
                    break;
                case "total_order_amount":
                    aValue = a?.total_order_amount || 0;
                    bValue = b?.total_order_amount || 0;
                    break;
                case "last_login":
                    aValue = a?.last_login ? moment(a.last_login).valueOf() : 0;
                    bValue = b?.last_login ? moment(b.last_login).valueOf() : 0;
                    break;
                case "last_order_date":
                    aValue = a?.last_order_date ? moment(a.last_order_date).valueOf() : 0;
                    bValue = b?.last_order_date ? moment(b.last_order_date).valueOf() : 0;
                    break;
                case "created_at":
                default:
                    aValue = a?.created_at ? moment(a.created_at).valueOf() : 0;
                    bValue = b?.created_at ? moment(b.created_at).valueOf() : 0;
                    break;
            }

            if (sortOrder === "asc") {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });

        return data;
    }, [CustomerListdata, searchText, statusFilter, hasOrdersFilter, dateFrom, dateTo, sortField, sortOrder]);

    var columns = [
        {
            name: <b>Created Date</b>,
            header: <b>Created Date</b>,
            selector: row => moment(row?.created_at).format('DD-MM-YYYY'),
            style: {
                textAlign: "center",
            },
            width: "150px",
        },
        {
            name: <b>Name</b>,
            header: <b>Name</b>,
            selector: row => row?.name || "-",
            style: {
                textAlign: "left",
            },
        },
        {
            name: <b>Email Address</b>,
            header: <b>Email Address</b>,
            selector: row => row?.email || "-",
            style: {
                textAlign: "left",
            },
        },
        {
            name: <b>Mobile</b>,
            header: <b>Mobile</b>,
            selector: row => row?.mobile || "-",
            style: {
                textAlign: "center",
            },
        },
        {
            name: <b>Total Orders</b>,
            header: <b>Total Orders</b>,
            selector: row => row?.total_orders ?? 0,
            style: {
                textAlign: "center",
            },
            width: "100px",
        },
        {
            name: <b>Total Price</b>,
            header: <b>Total Price</b>,
            selector: row => formatCurrency(row?.total_order_amount),
            style: {
                textAlign: "center",
            },
            width: "110px",
        },
        {
            name: <b>Last Purchased</b>,
            header: <b>Last Purchased</b>,
            selector: row => row?.last_order_date ? moment(row?.last_order_date).format('DD-MM-YYYY') : "-",
            style: {
                textAlign: "center",
            },
            width: "120px",
        },
        {
            name: <b>Last Login</b>,
            header: <b>Last Login</b>,
            selector: row => row?.last_login ? moment(row?.last_login).format('DD-MM-YYYY HH:mm') : "-",
            style: {
                textAlign: "center",
            },
            width: "150px",
        },
        {
            name: <b>Actions</b>,
            header: <b>Actions</b>,
            cell: (row) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <span
                        onClick={() => handleView(row)}
                        style={{ cursor: "pointer", color: "#007bff" }}
                        title="View Details"
                    >
                        <Eye size={18} />
                    </span>
                </div>
            ),
            style: {
                textAlign: "center",
            },
            width: "100px",
        },
    ];

    useEffect(() => {
        dispatch(GetCustomerList());
    }, []);

    const filterStyles = {
        label: {
            fontSize: "12px",
            fontWeight: 500,
            color: "#6c757d",
            marginBottom: "4px"
        },
        input: {
            fontSize: "13px",
            height: "38px"
        },
        select: {
            fontSize: "13px",
            height: "38px"
        }
    };

    return (
        <Fragment>
            <Breadcrumb title="Customer" parent="Products" />
            <Container fluid="true">
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5>Customer List</h5>
                            </CardHeader>
                            <CardBody>
                                {/* Filters Section */}
                                <div style={{
                                    backgroundColor: "#f8f9fa",
                                    padding: "16px",
                                    borderRadius: "8px",
                                    marginBottom: "20px"
                                }}>
                                    <Row>
                                        <Col md="3">
                                            <FormGroup style={{ marginBottom: "12px" }}>
                                                <Label style={filterStyles.label}>Search</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Name, Email or Mobile"
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    style={filterStyles.input}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup style={{ marginBottom: "12px" }}>
                                                <Label style={filterStyles.label}>Status</Label>
                                                <Input
                                                    type="select"
                                                    value={statusFilter}
                                                    onChange={(e) => setStatusFilter(e.target.value)}
                                                    style={filterStyles.select}
                                                >
                                                    <option value="">All Status</option>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup style={{ marginBottom: "12px" }}>
                                                <Label style={filterStyles.label}>Orders</Label>
                                                <Input
                                                    type="select"
                                                    value={hasOrdersFilter}
                                                    onChange={(e) => setHasOrdersFilter(e.target.value)}
                                                    style={filterStyles.select}
                                                >
                                                    <option value="">All Customers</option>
                                                    <option value="with_orders">With Orders</option>
                                                    <option value="no_orders">No Orders</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup style={{ marginBottom: "12px" }}>
                                                <Label style={filterStyles.label}>From Date</Label>
                                                <Input
                                                    type="date"
                                                    value={dateFrom}
                                                    onChange={(e) => setDateFrom(e.target.value)}
                                                    style={filterStyles.input}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup style={{ marginBottom: "12px" }}>
                                                <Label style={filterStyles.label}>To Date</Label>
                                                <Input
                                                    type="date"
                                                    value={dateTo}
                                                    onChange={(e) => setDateTo(e.target.value)}
                                                    style={filterStyles.input}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="1" style={{ display: "flex", alignItems: "flex-end" }}>
                                            <FormGroup style={{ marginBottom: "12px" }}>
                                                <Button
                                                    color="secondary"
                                                    outline
                                                    size="sm"
                                                    onClick={resetFilters}
                                                    title="Reset Filters"
                                                    style={{ height: "38px", width: "38px", padding: 10 }}
                                                >
                                                    <RefreshCw size={16} />
                                                </Button>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md="3">
                                            <FormGroup style={{ marginBottom: 0 }}>
                                                <Label style={filterStyles.label}>Sort By</Label>
                                                <Input
                                                    type="select"
                                                    value={sortField}
                                                    onChange={(e) => setSortField(e.target.value)}
                                                    style={filterStyles.select}
                                                >
                                                    <option value="created_at">Created Date</option>
                                                    <option value="name">Name</option>
                                                    <option value="email">Email</option>
                                                    <option value="total_orders">Total Orders</option>
                                                    <option value="total_order_amount">Total Price</option>
                                                    <option value="last_order_date">Last Purchased</option>
                                                    <option value="last_login">Last Login</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="2">
                                            <FormGroup style={{ marginBottom: 0 }}>
                                                <Label style={filterStyles.label}>Order</Label>
                                                <Input
                                                    type="select"
                                                    value={sortOrder}
                                                    onChange={(e) => setSortOrder(e.target.value)}
                                                    style={filterStyles.select}
                                                >
                                                    <option value="desc">Descending</option>
                                                    <option value="asc">Ascending</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="7" style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                                            <span style={{ fontSize: "13px", color: "#6c757d" }}>
                                                Showing {filteredData.length} of {CustomerListdata?.length || 0} customers
                                            </span>
                                        </Col>
                                    </Row>
                                </div>

                                <div className="clearfix"></div>
                                <div id="basicScenario" className="product-physical">
                                    {CustomerListdata && (
                                        <DataTables
                                            columns={columns}
                                            row={filteredData}
                                        />
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Customer Details Modal */}
            <Modal isOpen={viewModal} toggle={closeModal} size="lg" centered>
                <ModalHeader toggle={closeModal} style={{ borderBottom: "1px solid #eee" }}>
                    <span style={{ fontWeight: 600, fontSize: "1.1rem" }}>Customer Details</span>
                </ModalHeader>
                <ModalBody style={{ padding: "24px" }}>
                    {selectedCustomer && (
                        <div>
                            {/* Customer Basic Info */}
                            <Row>
                                <Col md="6">
                                    <div style={{ marginBottom: "20px" }}>
                                        <h6 style={{
                                            color: "#6c757d",
                                            fontSize: "12px",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            marginBottom: "16px",
                                            fontWeight: 600
                                        }}>
                                            Basic Information
                                        </h6>
                                        <Table borderless size="sm" style={{ marginBottom: 0 }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ color: "#6c757d", width: "40%", padding: "6px 0" }}>Name</td>
                                                    <td style={{ fontWeight: 500, padding: "6px 0" }}>{selectedCustomer?.name || "-"}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#6c757d", padding: "6px 0" }}>Email</td>
                                                    <td style={{ fontWeight: 500, padding: "6px 0" }}>{selectedCustomer?.email || "-"}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#6c757d", padding: "6px 0" }}>Mobile</td>
                                                    <td style={{ fontWeight: 500, padding: "6px 0" }}>{selectedCustomer?.mobile || "-"}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#6c757d", padding: "6px 0" }}>Status</td>
                                                    <td style={{ padding: "6px 0" }}>
                                                        <span style={{
                                                            backgroundColor: selectedCustomer?.status === "active" ? "#d4edda" : "#f8d7da",
                                                            color: selectedCustomer?.status === "active" ? "#155724" : "#721c24",
                                                            padding: "3px 10px",
                                                            borderRadius: "12px",
                                                            fontSize: "12px",
                                                            fontWeight: 500
                                                        }}>
                                                            {Capitalize(selectedCustomer?.status) || "-"}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#6c757d", padding: "6px 0" }}>Created Date</td>
                                                    <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                        {selectedCustomer?.created_at ? moment(selectedCustomer?.created_at).format('DD-MM-YYYY') : "-"}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#6c757d", padding: "6px 0" }}>Last Login</td>
                                                    <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                        {selectedCustomer?.last_login ? moment(selectedCustomer?.last_login).format('DD-MM-YYYY HH:mm') : "-"}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div style={{ marginBottom: "20px" }}>
                                        <h6 style={{
                                            color: "#6c757d",
                                            fontSize: "12px",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            marginBottom: "16px",
                                            fontWeight: 600
                                        }}>
                                            Order Statistics
                                        </h6>
                                        <Table borderless size="sm" style={{ marginBottom: 0 }}>
                                            <tbody>
                                                <tr>
                                                    <td style={{ color: "#6c757d", width: "50%", padding: "6px 0" }}>Total Orders</td>
                                                    <td style={{ fontWeight: 600, padding: "6px 0", fontSize: "16px" }}>
                                                        {selectedCustomer?.total_orders ?? 0}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#6c757d", padding: "6px 0" }}>Total Spent</td>
                                                    <td style={{ fontWeight: 600, padding: "6px 0", fontSize: "16px", color: "#28a745" }}>
                                                        {formatCurrency(selectedCustomer?.total_order_amount)}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ color: "#6c757d", padding: "6px 0" }}>Last Purchase</td>
                                                    <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                        {selectedCustomer?.last_order_date
                                                            ? moment(selectedCustomer?.last_order_date).format('DD-MM-YYYY HH:mm')
                                                            : "-"}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>

                            {/* Address Section */}
                            {selectedCustomer?.address && (
                                <div style={{
                                    marginTop: "8px",
                                    paddingTop: "20px",
                                    borderTop: "1px solid #eee"
                                }}>
                                    <h6 style={{
                                        color: "#6c757d",
                                        fontSize: "12px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        marginBottom: "16px",
                                        fontWeight: 600
                                    }}>
                                        Address
                                        {selectedCustomer?.address?.address_type && (
                                            <span style={{
                                                backgroundColor: "#e9ecef",
                                                color: "#495057",
                                                padding: "2px 8px",
                                                borderRadius: "10px",
                                                fontSize: "10px",
                                                marginLeft: "8px",
                                                textTransform: "capitalize"
                                            }}>
                                                {selectedCustomer?.address?.address_type}
                                            </span>
                                        )}
                                        {selectedCustomer?.address?.default && (
                                            <span style={{
                                                backgroundColor: "#cce5ff",
                                                color: "#004085",
                                                padding: "2px 8px",
                                                borderRadius: "10px",
                                                fontSize: "10px",
                                                marginLeft: "6px"
                                            }}>
                                                Default
                                            </span>
                                        )}
                                    </h6>
                                    <Row>
                                        <Col md="6">
                                            <Table borderless size="sm" style={{ marginBottom: 0 }}>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ color: "#6c757d", width: "40%", padding: "6px 0" }}>Name</td>
                                                        <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                            {selectedCustomer?.address?.first_name} {selectedCustomer?.address?.last_name}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ color: "#6c757d", padding: "6px 0" }}>Mobile</td>
                                                        <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                            {selectedCustomer?.address?.mobile || "-"}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ color: "#6c757d", padding: "6px 0" }}>Full Address</td>
                                                        <td style={{ fontWeight: 500, padding: "6px 0", lineHeight: "1.5" }}>
                                                            {getFullAddress(selectedCustomer?.address)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Col>
                                        <Col md="6">
                                            <Table borderless size="sm" style={{ marginBottom: 0 }}>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ color: "#6c757d", width: "40%", padding: "6px 0" }}>Pincode</td>
                                                        <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                            {selectedCustomer?.address?.pincode || "-"}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ color: "#6c757d", padding: "6px 0" }}>State</td>
                                                        <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                            {selectedCustomer?.address?.state || "-"}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ color: "#6c757d", padding: "6px 0" }}>Country</td>
                                                        <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                            {selectedCustomer?.address?.country || "-"}
                                                        </td>
                                                    </tr>
                                                    {selectedCustomer?.address?.comments && (
                                                        <tr>
                                                            <td style={{ color: "#6c757d", padding: "6px 0" }}>Comments</td>
                                                            <td style={{ fontWeight: 500, padding: "6px 0" }}>
                                                                {selectedCustomer?.address?.comments}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </Fragment>
    );
};

export default CustomerList;
